import React from 'react';
import { Link } from 'react-router-dom';
import { DocPage, Note, Steps, WqTable } from '../../components/documentation/web-integration';

const CONTENTS = ['Introduction', 'Ways to integrate', 'Payment flow', 'Authentication'];

const PATH_TO_LIVE = [
    <>
        <strong>Create your account.</strong> Every new WayaQuick account starts in <strong>Test mode</strong>, so you
        can build and try everything without moving real money.
    </>,
    <>
        <strong>Get your test API keys</strong> from the dashboard —{' '}
        <Link to="/api-encryption" className="wq-link">
            see how
        </Link>
        .
    </>,
    <>
        <strong>Build and test your integration</strong> with the{' '}
        <Link to="/test-card-information" className="wq-link">
            test cards
        </Link>
        .
    </>,
    <>
        <strong>Complete your KYC.</strong> Once it's approved, you can switch your dashboard to{' '}
        <strong>Live mode</strong>.
    </>,
    <>
        <strong>Generate your live API keys</strong> and replace the test keys in your integration. Nothing else in
        your code needs to change.
    </>,
];

const INTEGRATION_OPTIONS = [
    [
        <strong>Payment links, invoices &amp; subscription links</strong>,
        'Selling without a website or code — share a link by WhatsApp, SMS or email',
        <Link to="/payment-link" className="wq-link">
            Payment links
        </Link>,
    ],
    [
        <strong>WooCommerce plugin</strong>,
        'WordPress stores — no code',
        <Link to="/sdks/woocommerce" className="wq-link">
            WooCommerce plugin
        </Link>,
    ],
    [
        <strong>Server SDKs</strong>,
        'Your backend: PHP, .NET, Java, Node.js or Go',
        <Link to="/sdks?type=server" className="wq-link">
            Server SDKs
        </Link>,
    ],
    [
        <strong>App SDKs</strong>,
        'Building your own merchant app with Ionic, React Native or Flutter',
        <Link to="/sdks?type=merchant-app" className="wq-link">
            Merchant app SDKs
        </Link>,
    ],
    [
        <strong>Mobile SDKs</strong>,
        'Taking payments in your iOS, Android or Flutter app',
        <Link to="/sdks?type=mobile" className="wq-link">
            Mobile SDKs
        </Link>,
    ],
    [
        <strong>Standard API</strong>,
        'Any language — call the Merchant API directly over HTTPS',
        <Link to="/standard-api-integration" className="wq-link">
            Standard API Integration
        </Link>,
    ],
];

const PAYMENT_FLOW = [
    <>
        <strong>Create the payment.</strong> Your server sends the amount and your own unique reference to WayaQuick.
        WayaQuick returns a secure checkout link. No money moves at this stage.
    </>,
    <>
        <strong>The customer pays.</strong> They open the checkout link and pay by dynamic virtual account, bank
        transfer, card, USSD or PayAttitude. They must pay the exact amount — any other amount is rejected
        automatically, and if the money has already come through it is reversed to the customer.
    </>,
    <>
        <strong>WayaQuick confirms the result.</strong> WayaQuick sends a webhook to your server with the final
        status. You can also check the status at any time with your reference.
    </>,
    <>
        <strong>You fulfil the order</strong> — only once the status is <code>SUCCESSFUL</code>.
    </>,
    <>
        <strong>You get paid.</strong> Successful payments are settled to the default settlement account you set
        under Settings › WayaQuick Settings › Settlement Account.
    </>,
];

const STATUSES = {
    columns: ['Status', 'What it means', 'What to do'],
    rows: [
        [
            <code>PENDING</code>,
            'Still in progress, for example waiting for the bank to confirm.',
            "Wait for the webhook or check again later. Don't fulfil or refund yet.",
        ],
        [<code>SUCCESSFUL</code>, 'The customer paid. Funds are confirmed.', 'Fulfil the order.'],
        [<code>FAILED</code>, 'Declined, abandoned or rejected. The customer was not debited.', "Don't fulfil."],
        [<code>REFUNDED</code>, 'A successful payment that has been refunded.', 'No further action.'],
    ],
};

function GetStarted() {
    return (
        <DocPage
            seo={{
                title: 'Developer Getting Started',
                description:
                    'Start integrating WayaQuick: ways to integrate, how a payment flows through the API, and how authentication works.',
            }}
            eyebrow="Developers · Start here"
            title={
                <>
                    WayaQuick — <em>Getting Started</em>
                </>
            }
            contents={CONTENTS}
            footer="WayaQuick · Getting Started · Build in Test mode, then go live once your KYC is approved."
            prev={{ to: '/payment-link', label: 'WayaQuick Payment Links' }}
            next={{ to: '/test-environment', label: 'Test Environment' }}
        >
            <section className="wq-section" id={CONTENTS[0]}>
                <h2 className="wq-h2">Introduction</h2>
                <p className="wq-lead">
                    WayaQuick lets you collect payments from your customers and send payouts to bank accounts. You can
                    start without writing any code, or build WayaQuick into your website, app or backend.
                </p>

                <h4 className="wq-h4">What you can do with the API</h4>
                <ul className="wq-p wq-list">
                    <li>Collect payments through the secure WayaQuick checkout.</li>
                    <li>Send payouts to any Nigerian bank account.</li>
                    <li>Confirm an account name before paying out.</li>
                    <li>Verify a customer's identity with their BVN.</li>
                    <li>Receive webhooks when a payment succeeds or fails.</li>
                </ul>

                <h4 className="wq-h4">From sign-up to live payments</h4>
                <Steps items={PATH_TO_LIVE} />
            </section>

            <section className="wq-section" id={CONTENTS[1]}>
                <h2 className="wq-h2">Ways to integrate</h2>
                <p className="wq-p">Pick the option that fits how you sell. You can use more than one.</p>
                <WqTable columns={['Option', 'Best for', 'Start here']} rows={INTEGRATION_OPTIONS} />
                <Note label="Hosted checkout">
                    Every option uses the WayaQuick hosted checkout, so customers enter their payment details on
                    WayaQuick, not on your site. You don't need to handle card data yourself.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[2]}>
                <h2 className="wq-h2">Payment flow</h2>
                <p className="wq-p">Every WayaQuick payment goes through the same steps:</p>
                <Steps items={PAYMENT_FLOW} />

                <h4 className="wq-h4">Payment statuses</h4>
                <WqTable columns={STATUSES.columns} rows={STATUSES.rows} />
                <p className="wq-p">
                    These are the main statuses. The{' '}
                    <Link to="/standard-api-integration" className="wq-link">
                        Standard API Integration
                    </Link>{' '}
                    lists every status the API can return.
                </p>

                <h4 className="wq-h4">Refunds</h4>
                <p className="wq-p">
                    Refunds are sent from your dashboard. Open a successful transaction and click{' '}
                    <strong>Refund Customer</strong>: a payout opens with the amount already filled in. Only successful
                    payments can be refunded.
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[3]}>
                <h2 className="wq-h2">Authentication</h2>
                <p className="wq-p">
                    Every API request is authenticated with two headers — the same for web, mobile and server
                    integrations:
                </p>
                <WqTable
                    columns={['Header', 'Value']}
                    rows={[
                        [
                            <code>Authorization</code>,
                            <>
                                <code>Bearer</code> followed by your secret key
                            </>,
                        ],
                        [<code>X-Merchant-Id</code>, 'Your Merchant ID (starts with MER_)'],
                    ]}
                />
                <p className="wq-p">
                    The plugins and SDKs add these headers for you — you only supply your Merchant ID and secret key.
                    The key you use decides the mode: a test key runs test payments, a live key runs real ones.
                </p>
                <Note label="Server-side only">
                    Your secret key can move money from your account. Keep it on your server and never put it in
                    website code that runs in the customer's browser.
                </Note>
            </section>
        </DocPage>
    );
}

export default GetStarted;
